import { useEffect, useState } from 'react';
import { cloudApi } from '../services/api';
import {
  buildCloudStorageMeta,
  googleDriveFolderUrl,
  isLiveCloudProvider,
  isLocalIntentFileId,
  fileRoleLabel,
  type CloudStorageMeta,
  type Tier2SelectedFile,
} from '../lib/cloudStorage';

interface Tier2DocumentPickerProps {
  scope?: 'user' | 'org';
  cloud: CloudStorageMeta;
  category?: string;
  buttonLabel?: string;
  onFilesChange?: (files: Tier2SelectedFile[]) => void;
}

export default function Tier2DocumentPicker({
  scope = 'user',
  cloud,
  category = 'General',
  buttonLabel = 'Select General Documents',
  onFilesChange,
}: Tier2DocumentPickerProps) {
  const [selected, setSelected] = useState<Tier2SelectedFile[]>([]);
  const [driveFiles, setDriveFiles] = useState<Array<{ id: string; name: string; mimeType?: string }>>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [liveCloud, setLiveCloud] = useState(cloud);

  const liveGoogle = isLiveCloudProvider(liveCloud.provider)
    && liveCloud.connected
    && Boolean(liveCloud.folder_id);

  const refreshCloud = async () => {
    try {
      const data = await cloudApi.status(scope);
      const next = buildCloudStorageMeta(data.cloud_storage || {});
      setLiveCloud(next);
      return next;
    } catch {
      setLiveCloud(cloud);
      return cloud;
    }
  };

  useEffect(() => {
    void refreshCloud();
  }, [scope, cloud.connected, cloud.folder_id, cloud.provider]);

  useEffect(() => {
    cloudApi.getSelectedFiles(scope)
      .then((data) => {
        const files = (data.files || []) as Tier2SelectedFile[];
        const next = category === 'Emergency Protocol'
          ? files.filter((file) => file.category === 'Emergency Protocol')
          : files.filter((file) => file.category !== 'Emergency Protocol');
        setSelected(next);
        onFilesChange?.(files);
      })
      .catch(() => {});
  }, [scope, category]);

  const persist = async (files: Tier2SelectedFile[]) => {
    const existing = await cloudApi.getSelectedFiles(scope).catch(() => ({ files: [] }));
    const prior = (existing.files || []) as Tier2SelectedFile[];
    const kept = prior.filter((file) => {
      if (category === 'Emergency Protocol') return file.category !== 'Emergency Protocol';
      return file.category === 'Emergency Protocol';
    });
    const incomingNames = new Set(files.map((file) => file.filename.toLowerCase()));
    const cleanedKept = kept.filter((file) => {
      if (!isLocalIntentFileId(file.drive_file_id)) return true;
      return !incomingNames.has(file.filename.toLowerCase());
    });
    const seen = new Set<string>();
    const merged = [...cleanedKept, ...files].filter((file) => {
      const key = file.drive_file_id || file.filename;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    await cloudApi.saveSelectedFiles({ scope, files: merged });
    setSelected(files);
    onFilesChange?.(merged);
  };

  const openLocalStubPicker = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = category !== 'Emergency Protocol';
    input.onchange = () => {
      const list = input.files ? Array.from(input.files) : [];
      const uploadedAt = new Date().toISOString();
      const mapped: Tier2SelectedFile[] = list.map((file) => ({
        drive_file_id: `local-intent-${file.name}-${Date.now()}`,
        filename: file.name,
        category,
        role: category,
        selected_at: uploadedAt,
      }));
      void persist(mapped).then(() => {
        setStatus(
          'Name saved only. Copy the real file into your Google Drive folder, then connect Drive and select it again.',
        );
      });
    };
    input.click();
  };

  const openDrivePicker = async () => {
    setLoading(true);
    setStatus('');
    try {
      const current = await refreshCloud();
      if (!isLiveCloudProvider(current.provider) || !current.connected || !current.folder_id) {
        setStatus('Google Drive is not connected yet. Connect Drive first, then select files from that folder.');
        return;
      }
      const data = await cloudApi.listFiles(scope);
      setDriveFiles(data.files || []);
      setPickerOpen(true);
      if (!(data.files || []).length) {
        setStatus(`The Drive folder “${current.folder_label || 'Mentor_Files'}” is empty. Copy the Word files into that folder in Google Drive, wait for sync, then click this button again.`);
      }
    } catch (error: unknown) {
      const err = error as { error?: string; connected?: boolean };
      setStatus(err.error || 'Unable to browse Google Drive folder.');
      if (err.connected === false) {
        setPickerOpen(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleDriveFile = (file: { id: string; name: string; mimeType?: string }) => {
    setSelected((current) => {
      const exists = current.some((item) => item.drive_file_id === file.id);
      if (category === 'Emergency Protocol') {
        return exists
          ? []
          : [{
            drive_file_id: file.id,
            filename: file.name,
            category,
            role: category,
            mime_type: file.mimeType || null,
            selected_at: new Date().toISOString(),
          }];
      }
      if (exists) return current.filter((item) => item.drive_file_id !== file.id);
      return [
        ...current.filter((item) => !isLocalIntentFileId(item.drive_file_id) || item.filename !== file.name),
        {
          drive_file_id: file.id,
          filename: file.name,
          category,
          role: category,
          mime_type: file.mimeType || null,
          selected_at: new Date().toISOString(),
        },
      ];
    });
  };

  const saveDriveSelection = async () => {
    setLoading(true);
    try {
      const driveOnly = selected.filter((file) => !isLocalIntentFileId(file.drive_file_id));
      await persist(driveOnly);
      setPickerOpen(false);
      setStatus('Mentology will use the ticked files from your Google Drive folder. The documents stay in Drive.');
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Unable to save selected files.');
    } finally {
      setLoading(false);
    }
  };

  const removeSelected = async (fileId: string) => {
    const remaining = selected.filter((file) => file.drive_file_id !== fileId);
    try {
      await persist(remaining);
      setStatus('Removed from Mentology. The file is still in Google Drive.');
    } catch (error: unknown) {
      const err = error as { error?: string };
      setStatus(err.error || 'Unable to remove that file from Mentology.');
    }
  };

  const folderUrl = googleDriveFolderUrl(liveCloud.folder_id);

  return (
    <div className="tier2-doc-picker">
      <button
        type="button"
        className="btn-primary pro-setup-upload-btn"
        onClick={() => {
          if (liveGoogle) void openDrivePicker();
          else openLocalStubPicker();
        }}
        disabled={loading}
      >
        {loading ? 'Loading...' : liveGoogle ? `Choose files from ${liveCloud.folder_label || 'Google Drive'}` : buttonLabel}
      </button>
      <p className="pro-setup-helper-line">
        {liveGoogle
          ? `Opens a checklist of files already in Google Drive folder “${liveCloud.folder_label}”. It does not upload from your PC. `
          : 'Google Drive is not connected. This only saves the filename until you connect Drive.'}
        {liveGoogle && (
          <a href={folderUrl} target="_blank" rel="noreferrer">Open folder in Google Drive</a>
        )}
      </p>
      {selected.length > 0 && (
        <ul className="pro-setup-upload-list">
          {selected.map((doc) => (
            <li key={doc.drive_file_id}>
              <span>{doc.filename}</span>
              <span>
                {fileRoleLabel(doc.role || doc.category)}
                {' · '}
                {isLocalIntentFileId(doc.drive_file_id)
                  ? 'Name only — not in Drive yet'
                  : `In ${liveCloud.folder_label || 'Google Drive'}`}
                {' '}
                <button
                  type="button"
                  className="tier2-remove-btn"
                  onClick={() => void removeSelected(doc.drive_file_id)}
                >
                  Remove
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {pickerOpen && (
        <div className="tier2-drive-picker">
          <p className="pro-setup-section-subheading">
            Files currently in {liveCloud.folder_label || 'Google Drive'}
          </p>
          {driveFiles.length === 0 ? (
            <p className="pro-setup-helper-line">
              Nothing in this Drive folder yet. Copy the documents into the folder, wait for Google Drive to sync, then click the button again.
            </p>
          ) : (
            <ul className="pro-setup-upload-list">
              {driveFiles.map((file) => {
                const checked = selected.some((item) => item.drive_file_id === file.id);
                return (
                  <li key={file.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleDriveFile(file)}
                      />
                      {' '}
                      {file.name}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            className="btn-primary pro-setup-upload-btn"
            onClick={() => void saveDriveSelection()}
            disabled={loading || driveFiles.length === 0}
          >
            Use ticked Drive files
          </button>
        </div>
      )}
      {status && <p className="pro-setup-helper-line">{status}</p>}
    </div>
  );
}

export function cloudMetaFromForm(form: {
  cloud_provider: string;
  cloud_folder_label: string;
  cloud_folder_id?: string | null;
  cloud_connected?: boolean;
}): CloudStorageMeta {
  return buildCloudStorageMeta({
    provider: form.cloud_provider,
    folder_label: form.cloud_folder_label,
    folder_id: form.cloud_folder_id,
    connected: form.cloud_connected,
  });
}
